import React, { useEffect, useState } from "react";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Product } from "../../../types";
import { answerDeskService, FaqEntry } from "../../../services/answerDeskService";
import { apiErrorMessage } from "../answerDeskUtils";
import { ProductPicker } from "./ProductPicker";

interface AskManagerDialogProps {
    open: boolean;
    /** What the moderator searched for — prefilled as the customer's words. */
    searchText: string;
    products: Product[];
    onClose: () => void;
    onSent: (entry: FaqEntry) => void;
    /** Switch to the full editor with the answer the moderator already knows. */
    onSuggestInstead: (question: string, customerWords: string, products: Product[]) => void;
}

export const AskManagerDialog: React.FC<AskManagerDialogProps> = ({
    open,
    searchText,
    products,
    onClose,
    onSent,
    onSuggestInstead,
}) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const [question, setQuestion] = useState("");
    const [words, setWords] = useState("");
    const [picked, setPicked] = useState<Product[]>([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setQuestion("");
        setWords(searchText);
        setPicked(products);
        setError(null);
        setSaving(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const send = async () => {
        const q = question.trim() || words.trim();
        if (!q) {
            setError("Write the customer's question.");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const entry = await answerDeskService.createEntry({
                question: q,
                alt_phrasings: words.trim() && words.trim() !== q ? [words.trim()] : [],
                topic: picked.length ? "PRODUCT_INFO" : "OTHER",
                targets: picked.map((p) => ({ product_id: p.product_id })),
            });
            onSent(entry);
        } catch (err) {
            setError(apiErrorMessage(err, "Couldn't send it. Try again."));
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullScreen={fullScreen} maxWidth="sm" fullWidth>
            <DialogTitle>Ask a manager</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2.25}>
                    <Typography variant="body2" color="text.secondary">
                        Once a manager answers, it shows up here for everyone.
                    </Typography>
                    {error && <Alert severity="error">{error}</Alert>}
                    <TextField
                        label="Question"
                        placeholder="e.g. Can I pick up my order from the shop?"
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        inputProps={{ maxLength: 300 }}
                        autoFocus
                    />
                    <TextField
                        label="Customer's exact words (helps search later)"
                        value={words}
                        onChange={(e) => setWords(e.target.value)}
                        inputProps={{ maxLength: 120 }}
                    />
                    <ProductPicker
                        label="About a product? (optional)"
                        value={picked}
                        onChange={setPicked}
                        size="small"
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5, gap: 1, flexWrap: "wrap" }}>
                <Button
                    onClick={() => onSuggestInstead(question.trim() || words.trim(), words.trim(), picked)}
                    disabled={saving}
                    sx={{ mr: "auto" }}
                >
                    I know the answer — suggest it
                </Button>
                <Button onClick={onClose} disabled={saving} color="inherit">
                    Cancel
                </Button>
                <Button variant="contained" disableElevation onClick={send} disabled={saving}>
                    Send to the review queue
                </Button>
            </DialogActions>
        </Dialog>
    );
};
